import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import Tabs from "antd/es/tabs";
import capitalize from "lodash-es/capitalize";
import isEqual from "lodash-es/isEqual";
import { useIntl } from "react-intl";

import FieldFeature from "@mapmycustomers/shared/enum/fieldModel/FieldFeature";
import FieldType from "@mapmycustomers/shared/enum/fieldModel/FieldType";
import MarkerShape from "@mapmycustomers/shared/enum/MarkerShape";
import PinLegendClassificationType from "@mapmycustomers/shared/enum/pinLegends/PinLegendClassificationType";
import PinLegendDataLevel from "@mapmycustomers/shared/enum/pinLegends/PinLegendDataLevel";
import PinLegendDatingMethod from "@mapmycustomers/shared/enum/pinLegends/PinLegendDatingMethod";
import PinLegendType from "@mapmycustomers/shared/enum/PinLegendType";
import PinLegendVisibility from "@mapmycustomers/shared/enum/PinLegendVisibility";
import AnyColor from "@mapmycustomers/shared/types/AnyColor";
import { EntityTypesSupportedByMapsPage } from "@mapmycustomers/shared/types/entity";
import PinLegend, {
  ColorItemType,
  ColorPinLegend,
  CreateColorPinLegendPayload,
} from "@mapmycustomers/shared/types/map/PinLegend";
import Team from "@mapmycustomers/shared/types/Team";
import { UserRef } from "@mapmycustomers/shared/types/User";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import useDynamicCallback from "@mapmycustomers/shared/util/hook/useDynamicCallback";
import { EditableText } from "@mapmycustomers/ui";

import ExtendedAnalytics from "@app/component/analytics/ExtendedAnalytics";
import confirmExit from "@app/component/confirmExit";
import InterceptNavigationModal from "@app/component/InterceptNavigationModal";
import useMapView from "@app/component/view/utils/useMapView";
import {
  DEFAULT_NO_VALUE_COLOR,
  NEW_PIN_LEGEND_COLORS,
} from "@app/scene/map/components/pinLegends/utils/consts";
import Sidebar from "@app/scene/map/components/sidebar/Sidebar";
import { isCurrentUserOwner } from "@app/store/iam";
import { createColorPinLegend, updateColorPinLegend } from "@app/store/pinLegends/actions";
import { RootState } from "@app/store/rootReducer";
import { useExtendedAnalytics } from "@app/util/analytic/AnalyticsService";
import AnalyticsContext from "@app/util/contexts/AnalyticsContext";
import useAnalytics from "@app/util/contexts/useAnalytics";
import getFieldModelByEntityType from "@app/util/fieldModel/getByEntityType";
import { isCustomField } from "@app/util/fieldModel/impl/assert";
import loggingService from "@app/util/logging";
import { getEntityTypeAnalyticsName } from "@app/util/ui";

import styles from "./CommonStyles.module.scss";
import ConflictModal from "./components/ConflictModal";
import DeleteButton from "./components/DeleteButton";
import DetailsForm from "./components/DetailsForm";
import Footer from "./components/Footer";
import StyleEditor from "./components/StyleEditor";
import getSortedPinLegendItems from "./utils/getSortedPinLegendItems";

const getDefaults = (
  pinLegend: Partial<PinLegend> | PinLegend | undefined,
  initialEntityType: EntityTypesSupportedByMapsPage,
  defaultName: string = ""
) => {
  return {
    breakpoints: pinLegend?.thematic?.breakPoints,
    classification:
      pinLegend?.thematic?.classification ?? PinLegendClassificationType.EQUAL_INTERVAL,
    colors: pinLegend?.thematic?.colors ?? NEW_PIN_LEGEND_COLORS,
    creator: pinLegend?.user,
    dataLevel: pinLegend?.dataSelectionLevel ?? PinLegendDataLevel.ORGANIZATION,
    datingMethod: pinLegend?.thematic?.dateTrimming ?? PinLegendDatingMethod.ABSOLUTE,
    defaultColor: pinLegend?.thematic?.defaults?.color ?? DEFAULT_NO_VALUE_COLOR,
    entityType: pinLegend?.entity ?? initialEntityType,
    fieldName: pinLegend?.customField?.esKey ? pinLegend.customField.esKey : pinLegend?.fieldName,
    name: pinLegend?.name ?? defaultName,
    teamIds: pinLegend?.teamId ?? [],
    visibility: pinLegend?.visibility ?? PinLegendVisibility.PRIVATE,
  };
};

interface Props {
  entityType: EntityTypesSupportedByMapsPage;
  isOwner?: boolean;
  onCreateLegend: typeof createColorPinLegend.request;
  onHide: () => void;
  onUpdateLegend: typeof updateColorPinLegend.request;
  pinLegend?: Partial<PinLegend> | PinLegend; // PinLegend to edit if "id" defined, or just initial values
}

const CreateEditColorPinLegendDrawer: React.FC<Props> = ({
  entityType: initialEntityType,
  isOwner,
  onCreateLegend,
  onHide,
  onUpdateLegend,
  pinLegend,
}) => {
  const [originalPinLegend, setOriginalPinLegend] = useState(pinLegend);
  const creating = !originalPinLegend?.id;

  const analytics = useExtendedAnalytics(
    `${creating ? "Create" : "Edit"} custom color legend for ${getEntityTypeAnalyticsName(
      initialEntityType,
      false,
      true
    )}`,
    useAnalytics()
  );

  useEffect(() => {
    analytics.initiated("");
  }, [analytics]);

  const intl = useIntl();

  const [tab, setTab] = useState(creating ? "details" : "preferences");
  const handleTabClick = useCallback(
    (tab: string) => {
      setTab(tab);
      analytics.clicked([capitalize(tab)]);
    },
    [analytics]
  );

  const defaults = useMemo(
    () =>
      getDefaults(
        originalPinLegend,
        initialEntityType,
        intl.formatMessage({
          id: "map.colorization.createEditSidebar.color.title.default",
          defaultMessage: "New Pin Legend",
          description: "Default title of a new color pin legend",
        })
      ),
    [initialEntityType, intl, originalPinLegend]
  );

  const [classification, setClassification] = useState<PinLegendClassificationType>(
    defaults.classification
  );
  const [breakpoints, setBreakpoints] = useState<number[] | string[] | undefined>(
    defaults.breakpoints
  );
  const [dataLevel, setDataLevel] = useState<PinLegendDataLevel>(defaults.dataLevel);
  const [datingMethod, setDatingMethod] = useState<PinLegendDatingMethod>(defaults.datingMethod);
  const [name, setName] = useState<string>(defaults.name);
  const [entityType, setEntityType] = useState<EntityTypesSupportedByMapsPage>(defaults.entityType);
  const [fieldName, setFieldName] = useState<string | undefined>(defaults.fieldName);
  const [teamIds, setTeamIds] = useState<Team["id"][]>(defaults.teamIds);
  const [visibility, setVisibility] = useState<PinLegendVisibility>(defaults.visibility);
  const [colors, setColors] = useState<ColorItemType[]>(defaults.colors);
  const [defaultColor, setDefaultColor] = useState<AnyColor>(defaults.defaultColor);
  const [error, setError] = useState(false);

  useEffect(() => {
    setColors(
      getSortedPinLegendItems(
        originalPinLegend?.thematic?.colors,
        originalPinLegend?.entity,
        originalPinLegend?.fieldName,
        originalPinLegend?.customField?.esKey
      )
    );
  }, [
    originalPinLegend?.thematic?.colors,
    originalPinLegend?.entity,
    originalPinLegend?.fieldName,
    originalPinLegend?.customField?.esKey,
  ]);

  const [creator, setCreator] = useState<undefined | UserRef>(defaults.creator);
  useEffect(() => {
    if (originalPinLegend?.user) {
      setCreator(originalPinLegend.user);
    }
  }, [originalPinLegend?.user]);

  const handleFieldNameChange = useCallback(
    (fieldName: string | undefined) => {
      setFieldName(fieldName);
      const fieldModel = getFieldModelByEntityType(entityType);
      const field = fieldName ? fieldModel.getByName(fieldName) : undefined;

      setBreakpoints([]); // reset breakpoints

      if (
        originalPinLegend ||
        (field &&
          !(
            field.hasAnyFeature(FieldFeature.NUMERIC) ||
            field.type === FieldType.DATE ||
            field.type === FieldType.DATE_TIME
          ))
      ) {
        // we need to recreate colors when a new field is selected for a pin legend we're editing
        // or when user picks a non-option field
        setColors([]);
      } else {
        setColors(NEW_PIN_LEGEND_COLORS);
      }
    },
    [entityType, originalPinLegend]
  );

  const canSave = !!name.trim().length && entityType && fieldName && colors.length > 0 && !error;

  const hasChanges = useMemo(
    () =>
      !isEqual(defaults, {
        breakpoints,
        classification,
        colors,
        creator,
        dataLevel,
        datingMethod,
        defaultColor,
        entityType,
        fieldName,
        name,
        teamIds,
        visibility,
      }),
    [
      breakpoints,
      classification,
      colors,
      creator,
      dataLevel,
      datingMethod,
      defaultColor,
      defaults,
      entityType,
      fieldName,
      name,
      teamIds,
      visibility,
    ]
  );

  const { updateViewStateAndFetch } = useMapView();

  const resetStateTo = useCallback((value: typeof defaults) => {
    setClassification(value.classification);
    setBreakpoints(value.breakpoints);
    setDataLevel(value.dataLevel);
    setDatingMethod(value.datingMethod);
    setName(value.name);
    setEntityType(value.entityType);
    setFieldName(value.fieldName);
    setTeamIds(value.teamIds);
    setVisibility(value.visibility);
    setColors(value.colors);
    setDefaultColor(value.defaultColor);
    setCreator(value.creator);
  }, []);

  const [conflictModalVisible, showConflictModal, hideConflictModal] = useBoolean();

  const handleOk = useDynamicCallback(() => {
    if (!fieldName || !canSave) {
      return;
    }

    const fieldModel = getFieldModelByEntityType(entityType);
    const field = fieldModel.getByName(fieldName);
    if (!field) {
      loggingService.warning(
        `Invalid field selected in CreateEditColorPinLegendDrawer: ${entityType}.${fieldName}`,
        { field }
      );
      return;
    }

    const payload: CreateColorPinLegendPayload = {
      customFieldId: isCustomField(field) ? field.customFieldData.id : null,
      dataSelectionLevel: dataLevel,
      entity: entityType,
      fieldName: isCustomField(field) ? "" : fieldName,
      name,
      teamId: teamIds,
      thematic: {
        breakPoints: breakpoints,
        buckets: colors.length,
        classification,
        colors,
        dateTrimming: datingMethod,
        defaults: {
          color: defaultColor,
          shape: MarkerShape.SQUARE,
        },
      },
      type: PinLegendType.COLOR,
      visibility,
    };

    if (isOwner && !creating) {
      payload.user = creator;
    }

    if (!creating) {
      onUpdateLegend?.({
        callback: (pinLegend: PinLegend) => {
          setOriginalPinLegend(pinLegend);

          // Reset states to contain actual data
          resetStateTo(getDefaults(pinLegend, pinLegend.entity));

          analytics.completed("Updated", {
            entityType: getEntityTypeAnalyticsName(entityType, false, false),
            field: field.displayName,
            isNumeric: field.hasFeature(FieldFeature.NUMERIC),
            visibility,
          });
        },
        conflictCallback: showConflictModal,
        payload: { ...(originalPinLegend as ColorPinLegend), ...payload },
      });
    } else {
      onCreateLegend?.({
        callback: (pinLegend: PinLegend) => {
          setOriginalPinLegend(pinLegend);

          updateViewStateAndFetch({ request: { colorKey: { [entityType]: pinLegend.id } } });
          setTab("preferences");

          // Now reset states to contain actual data
          resetStateTo(getDefaults(pinLegend, pinLegend.entity));

          analytics.completed("Created", {
            entityType: getEntityTypeAnalyticsName(entityType, false, false),
            field: field.displayName,
            isNumeric: field.hasFeature(FieldFeature.NUMERIC),
            visibility,
          });
        },
        payload,
      });
    }
  });

  const handleCancel = useCallback(async () => {
    resetStateTo(defaults);
    analytics.cancelled("");
  }, [resetStateTo, defaults, analytics]);

  const handleHide = useCallback(async () => {
    if (hasChanges) {
      const shouldLeave = await confirmExit();
      if (!shouldLeave) {
        return;
      }
    }

    onHide();
    analytics.cancelled("");
  }, [analytics, onHide, hasChanges]);

  const handleReset = useCallback(
    (pinLegend: PinLegend) => {
      setOriginalPinLegend(pinLegend);
      resetStateTo(getDefaults(pinLegend, pinLegend.entity));
    },
    [resetStateTo]
  );

  return (
    <AnalyticsContext.Provider value={analytics}>
      <Sidebar
        className={styles.sidebar}
        mask
        maskClosable={false}
        onClose={handleHide}
        title={<EditableText onChange={setName} value={name} />}
        visible
      >
        <div className={styles.container}>
          <Tabs activeKey={tab} className={styles.tabs} onTabClick={handleTabClick}>
            <Tabs.TabPane disabled={!fieldName} key="preferences" tab="Preferences">
              <ExtendedAnalytics keyPath="Preferences">
                {fieldName && (
                  <StyleEditor
                    breakpoints={breakpoints}
                    classification={classification}
                    colors={colors}
                    dataLevel={dataLevel}
                    datingMethod={datingMethod}
                    defaultColor={defaultColor}
                    entityType={entityType}
                    fieldName={fieldName}
                    onBreakpointsChange={setBreakpoints}
                    onClassificationChange={setClassification}
                    onColorChange={setColors}
                    onDataLevelChange={setDataLevel}
                    onDatingMethodChange={setDatingMethod}
                    onDefaultColorChange={setDefaultColor}
                    onError={setError}
                    type={PinLegendType.COLOR}
                  />
                )}
              </ExtendedAnalytics>
            </Tabs.TabPane>
            <Tabs.TabPane className={styles.detailsTab} key="details" tab="Details">
              <ExtendedAnalytics keyPath="Details">
                <DetailsForm
                  creating={creating}
                  creator={creator}
                  entityType={entityType}
                  fieldName={fieldName}
                  onCreatorChange={setCreator}
                  onEntityTypeChange={setEntityType}
                  onFieldNameChange={handleFieldNameChange}
                  onTeamIdsChange={setTeamIds}
                  onVisibilityChange={setVisibility}
                  teamIds={teamIds}
                  type={PinLegendType.COLOR}
                  visibility={visibility}
                />
                {!creating && (
                  <DeleteButton onDeleted={onHide} pinLegend={originalPinLegend as PinLegend} />
                )}
              </ExtendedAnalytics>
            </Tabs.TabPane>
          </Tabs>
        </div>

        {hasChanges && (
          <Footer
            creating={creating}
            okDisabled={!canSave}
            onCancel={creating ? handleHide : handleCancel}
            onOk={handleOk}
            showConfirmation={visibility !== PinLegendVisibility.PRIVATE}
          />
        )}

        <InterceptNavigationModal
          onLeave={handleHide}
          shouldCloseOnLeave
          shouldShowPrompt={useDynamicCallback(() => hasChanges)}
        />

        {conflictModalVisible && (
          <ConflictModal
            // we know original legend exists coz conflict modal is only possible to appear
            // when we _update_ a pin legend
            id={originalPinLegend!.id!}
            name={name}
            onHide={hideConflictModal}
            onResetState={handleReset}
          />
        )}
      </Sidebar>
    </AnalyticsContext.Provider>
  );
};

const mapStateToProps = (state: RootState) => ({
  isOwner: isCurrentUserOwner(state),
});

const mapDispatchToProps = {
  onCreateLegend: createColorPinLegend.request,
  onUpdateLegend: updateColorPinLegend.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(CreateEditColorPinLegendDrawer);
